import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Button as ShadcnButton } from '@/components/shadcn/button';
import { Input } from '@/components/shadcn/input';
import {
  isQuickPickScoreDisabled,
  PADEL_MAX_SCORE,
  PADEL_QUICK_PICK_MAX,
} from './padelScoringUtils';

const QUICK_SCORES = Array.from({ length: PADEL_QUICK_PICK_MAX }, (_, i) => i + 1);

export interface PadelScorePickerBodyProps {
  teamLabel: string;
  value: number;
  targetScore?: number | null;
  americanoMode: boolean;
  onSelect: (score: number) => void;
  onClose: () => void;
}

export default function PadelScorePickerBody({
  teamLabel,
  value,
  targetScore,
  americanoMode,
  onSelect,
  onClose,
}: PadelScorePickerBodyProps) {
  const [customOpen, setCustomOpen] = useState(value > PADEL_QUICK_PICK_MAX);
  const [customValue, setCustomValue] = useState(value > PADEL_QUICK_PICK_MAX ? String(value) : '');

  useEffect(() => {
    if (value > PADEL_QUICK_PICK_MAX) {
      setCustomOpen(true);
      setCustomValue(String(value));
    } else {
      setCustomOpen(false);
      setCustomValue('');
    }
  }, [value]);

  const applyScore = (score: number) => {
    onSelect(score);
    onClose();
  };

  const handleCustomApply = () => {
    const parsed = Number.parseInt(customValue, 10);
    if (Number.isNaN(parsed) || parsed <= PADEL_QUICK_PICK_MAX || parsed > PADEL_MAX_SCORE) {
      return;
    }
    if (
      americanoMode &&
      targetScore != null &&
      isQuickPickScoreDisabled(parsed, targetScore, true)
    ) {
      return;
    }
    applyScore(parsed);
  };

  const customParsed = Number.parseInt(customValue, 10);
  const customValid =
    !Number.isNaN(customParsed) &&
    customParsed > PADEL_QUICK_PICK_MAX &&
    customParsed <= PADEL_MAX_SCORE &&
    (!americanoMode ||
      targetScore == null ||
      !isQuickPickScoreDisabled(customParsed, targetScore, true));

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center gap-1 pt-1">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
          <Icon icon="mdi:tennis" className="h-6 w-6 text-gray-700" />
        </div>
        <p className="text-center text-sm font-medium text-gray-900">
          Skor untuk <span className="font-semibold">{teamLabel}</span>?
        </p>
        {americanoMode && targetScore != null && (
          <p className="text-center text-xs text-gray-500">
            Target rally {targetScore} — skor lawan akan diisi otomatis
          </p>
        )}
      </div>

      <div className="grid grid-cols-5 gap-2">
        {QUICK_SCORES.map((score) => {
          const disabled = isQuickPickScoreDisabled(score, targetScore, americanoMode);
          const selected = value === score && value <= PADEL_QUICK_PICK_MAX;
          return (
            <ShadcnButton
              key={score}
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={() => applyScore(score)}
              className={`h-11 rounded-xl text-sm font-semibold ${
                selected
                  ? 'border-[#34C759] bg-[#34C759] text-white hover:bg-[#2db84e] hover:text-white'
                  : 'border-gray-200 bg-gray-50 text-gray-900 hover:bg-gray-100'
              }`}
            >
              {score}
            </ShadcnButton>
          );
        })}
      </div>

      <div className="space-y-2">
        <ShadcnButton
          type="button"
          variant="ghost"
          className="h-auto w-full justify-start px-0 text-sm font-medium text-gray-700 hover:bg-transparent"
          onClick={() => setCustomOpen((prev) => !prev)}
        >
          <Icon
            icon={customOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'}
            className="mr-1 h-4 w-4"
          />
          Lainnya (&gt;15)
        </ShadcnButton>
        {customOpen && (
          <div className="flex items-center gap-2">
            <Input
              inputMode="numeric"
              placeholder="16–32"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value.replace(/\D/g, ''))}
              className="h-11 text-center text-lg font-semibold"
            />
            <ShadcnButton
              type="button"
              className="h-11 shrink-0 bg-[#FF5A1F] hover:bg-[#e54e16]"
              disabled={!customValid}
              onClick={handleCustomApply}
            >
              Terapkan
            </ShadcnButton>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <ShadcnButton
          type="button"
          variant="outline"
          size="icon"
          className="h-11 w-11 shrink-0 border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600"
          aria-label="Hapus skor"
          onClick={() => applyScore(0)}
        >
          <Icon icon="mdi:trash-can-outline" className="h-5 w-5" />
        </ShadcnButton>
        <ShadcnButton type="button" variant="outline" className="h-11 flex-1" onClick={onClose}>
          Batal
        </ShadcnButton>
      </div>
    </div>
  );
}
